import {
  Compress,
  ForwardToInbox,
  PanTool,
  RestorePage,
  Settings,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { round, sum, uniq } from "lodash";
import { useMemo, useState } from "react";
import {
  ExtensionOperationContext,
  GetInvoiceListQuery,
  InvoiceStatus,
} from "../../../graphql/generated";
import LocaleProvider from "../../../providers/LocaleProvider";
import useDialog from "../../../utils/hooks/useDialog";
import { TableField } from "../../common/LynksTable/LynksTable";
import ShipmentLink from "../../common/ShipmentLink";
import SmartLynksTable, {
  QueryFn,
} from "../../common/SmartLynksTable/SmartLynksTable";
import { useTranslation } from "react-i18next";
import { useConfirm } from "material-ui-confirm";
import invoiceStatusLabel from "../../../utils/labels/invoiceStatusLabel";
import InvoiceBatchProcess from "../InvoiceBatchProcess";
import { getCustomFieldValue } from "../../../utils/customFields";
import { testIds } from "./InvoiceList.testIds";
import { formatDateTime } from "../../../utils/labels/formatDateTime";

export type InvoiceListItemData = GetInvoiceListQuery["invoices"]["data"][0];
type InvoiceListProps = {
  queryHook: QueryFn<InvoiceListItemData, "invoices">;
  onSendInvoice: (invoice: InvoiceListItemData) => void;
  onInvoiceStatusUpdate: (
    invoice: InvoiceListItemData,
    status: InvoiceStatus,
    closingNote?: string
  ) => void;
  onInvoicesConsolidate: (invoices: InvoiceListItemData[]) => void;
  onConfirmBatchProcess: (
    invoices: InvoiceListItemData[],
    statusBatchProcess: InvoiceStatus
  ) => void;
  batchProcessModalOpen: boolean;
  setBatchProcessModalOpen: (open: boolean) => void;
  isLoadingRequest: boolean;
};

export enum InvoiceStatusTab {
  NOT_INVOICED,
  IN_PROCESS,
  ON_HOLD,
  TRANSFERRED,
  INVOICED,
  PAID,
  CANCELLED,
}

const statusToInvoiceStatus: {
  [key in InvoiceStatusTab]: InvoiceStatus | null;
} = {
  [InvoiceStatusTab.NOT_INVOICED]: InvoiceStatus.NotInvoiced,
  [InvoiceStatusTab.IN_PROCESS]: InvoiceStatus.Draft,
  [InvoiceStatusTab.TRANSFERRED]: InvoiceStatus.Transferred,
  [InvoiceStatusTab.ON_HOLD]: InvoiceStatus.OnHold,
  [InvoiceStatusTab.INVOICED]: InvoiceStatus.Closed,
  [InvoiceStatusTab.PAID]: InvoiceStatus.Paid,
  [InvoiceStatusTab.CANCELLED]: InvoiceStatus.Cancelled,
};

const InvoiceList = ({
  queryHook: query,
  onSendInvoice,
  onInvoiceStatusUpdate,
  onInvoicesConsolidate,
  onConfirmBatchProcess,
  batchProcessModalOpen,
  setBatchProcessModalOpen,
  isLoadingRequest,
}: InvoiceListProps) => {
  const { showDialog, hideDialog } = useDialog();
  const [closingNoteModalOpen, setClosingNoteModalOpen] = useState(false);
  const [selectedInvoiceForClosing] =
    useState<InvoiceListItemData | null>(null);
  const [closingNote, setClosingNote] = useState("");
  const [statusTab, setStatusTab] = useState<InvoiceStatusTab>(
    InvoiceStatusTab.NOT_INVOICED
  );
  const [statusBatchProcess, setStatusBatchProcess] = useState<InvoiceStatus>(
    InvoiceStatus.OnHold
  );
  const { t } = useTranslation(["finance", "common"]);
  const [selected, setSelected] = useState<InvoiceListItemData[]>([]);
  const confirm = useConfirm();

  const fields: Array<TableField<InvoiceListItemData, InvoiceStatus>> = useMemo(
    () => [
      {
        label: "Status",
        type: "enum",
        value: (invoice) => invoice.status,
        valueLabel: invoiceStatusLabel,
        colors: {
          [InvoiceStatus.Draft]: "warning",
          [InvoiceStatus.Sent]: "primary",
          [InvoiceStatus.Paid]: "success",
          [InvoiceStatus.Cancelled]: "error",
          [InvoiceStatus.Closed]: "success",
          [InvoiceStatus.NotInvoiced]: "warning",
          [InvoiceStatus.OnHold]: "warning",
          [InvoiceStatus.Transferred]: "success",
          [InvoiceStatus.PendingCompletion]: "warning"
        },
        filterable: false,
      },
      {
        label: t("order"),
        type: "custom",
        value: (invoice) => {
          return (
            <Stack direction="row" spacing={2}>
              {(invoice.shipments || []).map((shipment) => (
                <ShipmentLink shipment={shipment} />
              ))}
            </Stack>
          );
        },
        rawValue: (invoice) => {
          return (invoice.shipments || [])
            .map((s) => {
              const shipmentNum = s.shipmentNumber;
              const refNums = (s.referenceNumbers || []).join(", ");
              return [shipmentNum, refNums].filter(Boolean).join(" / ");
            })
            .join(", ");
        },
        sortBy: "shipment.shipmentNumber",
      },
      {
        label: t("invoiceNumber"),
        type: "string",
        value: "invoiceNumber",
        sortBy: "invoiceNumber",
      },
      {
        label: t("amount"),
        type: "string",
        value: (invoice) =>
          invoice.status !== InvoiceStatus.NotInvoiced
            ? `${LocaleProvider.getCurrencySymbol()} ${round(
                sum(invoice.charges.map((charge) => charge.unit * charge.rate)),
                2
              )}`
            : "",
      },
      {
        label: t("customer"),
        type: "custom",
        value: (invoice) =>
          invoice.customer ? (
            <Link data-testid={testIds.invoiceListCustomer} href={`/customers/details/${invoice.customer._id}`}>
              {invoice.customer.name}
            </Link>
          ) : null,
        sortBy: "customer.name",
      },
      {
        label: t("shipper", "Shipper"),
        type: "custom",
        value: (invoice) => {
          const shippers = (invoice.shipments || []).flatMap((s) =>
            s.shipmentLocations.map((l) => l.shipper)
          );
          return (
            <Stack
              direction="row"
              alignItems="center"
              divider={
                <Typography
                  sx={{
                    mx: 0.5,
                  }}
                >
                  {" "}
                  /{" "}
                </Typography>
              }
            >
              {shippers.map((shipper) =>
                shipper ? (
                  <>
                    <Link data-testid={`${testIds.invoiceListShipper}-${shipper._id}`} href={`/locations/details/${shipper._id}`}>
                      {shipper.name}
                    </Link>
                  </>
                ) : null
              )}
            </Stack>
          );
        },
        rawValue: (invoice) => {
          const shippers = (invoice.shipments || []).flatMap((s) =>
            s.shipmentLocations.map((l) => l.shipper)
          );
          return shippers
            .filter(Boolean)
            .map((s) => s?.name)
            .join(", ");
        },
        sortBy: "shipper.name",
      },
      {
        label: t("receiver", "Receiver"),
        type: "custom",
        value: (invoice) => {
          const receivers = (invoice.shipments || []).flatMap((s) =>
            s.shipmentLocations.map((l) => l.receiver)
          );
          return (
            <Stack
              direction="row"
              alignItems="center"
              divider={
                <Typography
                  sx={{
                    mx: 0.5,
                  }}
                >
                  {" "}
                  /{" "}
                </Typography>
              }
            >
              {receivers.map((receiver) =>
                receiver ? (
                  <>
                    <Link data-testid={`${testIds.invoiceListReceiver}-${receiver._id}`} href={`/locations/details/${receiver._id}`}>
                      {receiver.name}
                    </Link>
                  </>
                ) : null
              )}
            </Stack>
          );
        },
        rawValue: (invoice) => {
          const receivers = (invoice.shipments || []).flatMap((s) =>
            s.shipmentLocations.map((l) => l.receiver)
          );
          return receivers
            .filter(Boolean)
            .map((r) => r?.name)
            .join(", ");
        },
        sortBy: "receiver.name",
      },
      {
        label: t("deliveryDate", "Delivery Date"),
        type: "datetime",
        value: (invoice) => {
          const rawDates = (invoice.shipments || [])
            .map((s) => s.route?.lastDropoffTime || s.trip?.lastDropoffTime)
            .filter(Boolean);
          const dates = uniq(rawDates.map((date) => formatDateTime(date)));
          if (dates.length <= 1) {
            return rawDates[0];
          }
          // A consolidated invoice shows the date of each of its orders.
          return (
            <Stack>
              {dates.map((date) => (
                <Typography key={date} variant="body2">
                  {date}
                </Typography>
              ))}
            </Stack>
          );
        },
        sortBy: "trip.lastDropoffTime",
      },
      {
        label: t("poNumber", "PO Number"),
        type: "string",
        value: (invoice) =>
          invoice.shipments
            ?.map((s) => s.purchaseOrderNumbers || [])
            .join(", "),
      },
      {
        label: t("referenceNumbers", "Reference Numbers"),
        type: "string",
        value: (invoice) =>
          invoice.shipments
            ?.flatMap((s) => s.referenceNumbers || [])
            .join(", "),
      },
      {
        label: t("bolNumbers", "BOL Numbers"),
        type: "string",
        value: (invoice) =>
          invoice.shipments
            ?.flatMap((s) => s.billOfLadingNumbers || [])
            .join(", "),
      },
      {
        label: t("ticketNumbers", "Ticket Numbers"),
        type: "string",
        value: (invoice) =>
          invoice.shipments?.flatMap((s) => s.ticketNumbers || []).join(", "),
      },
      {
        label: t("rateConNumbers", "Rate confirmation numbers"),
        type: "string",
        value: (invoice) =>
          invoice.shipments?.flatMap((s) => s.rateConNumbers || []).join(", "),
      },
      {
        label: t("tags", "Tags"),
        type: "string",
        value: (invoice) =>
          invoice.shipments?.flatMap((s) => s.tags || []).join(", "),
        sortBy: "shipment.tags",
      },
    ],
    [t]
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Tabs data-testid={testIds.invoiceListNotInvoicedNavigation}
          value={statusTab}
          onChange={(event, value) => {
            setStatusTab(value);
          }}
          aria-label="basic tabs example"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab data-testid={testIds.invoiceListNotInvoicedView}
            label={t("notInvoiced", "Not invoiced")}
            value={InvoiceStatusTab.NOT_INVOICED}
          />
          <Tab data-testid={testIds.invoiceListInProcess}
            label={t("inProcess", "In process")}
            value={InvoiceStatusTab.IN_PROCESS}
          />
          <Tab data-testid={testIds.invoiceListInvoiced}
            label={t("invoiced", "Invoiced")}
            value={InvoiceStatusTab.INVOICED}
          />
          <Tab data-testid={testIds.invoiceListPaid}
            label={t("paid", "Paid")}
            value={InvoiceStatusTab.PAID}
          />
          <Tab data-testid={testIds.invoiceListTransferred}
            label={t("transferred", "Transferred")}
            value={InvoiceStatusTab.TRANSFERRED}
          />
          <Tab data-testid={testIds.invoiceListOnHold}
            label={t("onHold", "On hold")}
            value={InvoiceStatusTab.ON_HOLD}
          />
          <Tab data-testid={testIds.invoiceListCancelled}
            label={t("cancelled", "Cancelled")}
            value={InvoiceStatusTab.CANCELLED}
          />
        </Tabs>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button data-testid={testIds.invoiceListBatchProcess}
          variant="contained"
          startIcon={<Settings />}
          onClick={() => {
            try {
            } catch (e) {}
            setBatchProcessModalOpen(true);
          }}
        >
          {t("batchProcess", "Batch Process")}
        </Button>
      </Box>
      <SmartLynksTable
        query={query}
        dataKey="invoices"
        id="invoice-list"
        fields={fields}
        extensionOperationContext={ExtensionOperationContext.Invoice}
        additionalQueryVariables={{
          status: statusToInvoiceStatus[statusTab],
        }}
        detailsUrlPrefix={`/invoices/details`}
        selectable
        showGroups
        onSelect={setSelected}
        getRecordLabel={(invoice) => invoice.invoiceNumber || invoice._id}
        actions={[
          {
            icon: <ForwardToInbox />,
            tooltip: t("sendInvoice"),
            disabled: (invoice) => {
              const ediInvoiceSelection = getCustomFieldValue(invoice.customer?.customFields, "edi_invoice_selection");
              if (!ediInvoiceSelection || ["email", "both"].includes(ediInvoiceSelection)) {
                return false;
              }
              return true;
            },
            isApplicable: (invoice) =>
              invoice.status !== InvoiceStatus.NotInvoiced,
            onClick: (invoice) => {
              if (!invoice.customer?.contact.email) {
                showDialog({
                  type: "error",
                  title: t("common:error.title"),
                  description: t("customerNoEmail"),
                });
                return;
              }
              showDialog({
                type: "primary",
                title: t("sendInvoice"),
                description: t("sendInvoiceDescription", {
                  customerName: invoice.customer.name,
                  customerEmail: invoice.customer.contact.email,
                }),
                actions: [
                  {
                    title: t("common:cancel"),
                    type: "secondary",
                  },
                  {
                    title: t("common:send", "Send"),
                    type: "primary",
                    onClick: () => {
                      onSendInvoice(invoice);
                      hideDialog();
                    },
                  },
                ],
              });
            },
          },
          {
            icon: <PanTool />,
            tooltip: t("putInvoiceOnHold"),
            isApplicable: (invoice) =>
              invoice.status === InvoiceStatus.Draft ||
              invoice.status === InvoiceStatus.Sent,
            onClick: (invoice) => {
              onInvoiceStatusUpdate(invoice, InvoiceStatus.OnHold);
            },
          },
          {
            icon: <RestorePage />,
            isApplicable: (invoice) => invoice.status === InvoiceStatus.Closed,
            tooltip: t("reopenInvoice"),
            onClick: async (invoice) => {
              onInvoiceStatusUpdate(invoice, InvoiceStatus.Draft, "");
            },
          },
        ]}
        bulkActionMenus={[
          {
            icon: <Compress />,
            id: "consolidate",
            label: t("consolidate", "Consolidate"),
            onClick: async () => {
              try {
                await confirm({
                  title: t("consolidateInvoices", "Consolidate Invoices"),
                  description: t(
                    "consolidateInvoicesDescription",
                    "Are you sure you want to consolidate these invoices ({{invoiceNumbers}}) ?",
                    {
                      invoiceNumbers: selected
                        .map((invoice) => invoice.invoiceNumber)
                        .join(", "),
                    }
                  ),
                });
                onInvoicesConsolidate(selected);
              } catch (error) {
                if (error) {
                  console.error(error);
                  throw error;
                }
              }
            },
          },
        ]}
      />
      <InvoiceBatchProcess
        queryHook={query}
        status={statusToInvoiceStatus[statusTab]}
        open={batchProcessModalOpen}
        onClose={() => setBatchProcessModalOpen(false)}
        onConfirm={async () => {
          onConfirmBatchProcess(selected, statusBatchProcess);
        }}
        setSelectedInvoices={setSelected}
        invoices={selected}
        onChange={({ status }) => {
          setStatusBatchProcess(status);
        }}
        statusBatchProcess={statusBatchProcess}
        isLoadingRequest={isLoadingRequest}
      />
      <Dialog open={closingNoteModalOpen}>
        <DialogTitle>{t("invoiceClosingNote")}</DialogTitle>
        <DialogContent
          sx={{
            pt: 1,
          }}
        >
          <Stack sx={{ mt: 1 }}>
            <TextField
              label={t("closingNote")}
              value={closingNote}
              onChange={(e) => {
                setClosingNote(e.target.value);
              }}
            inputProps={{ "data-testid": testIds.invoiceListClosingNote } as React.InputHTMLAttributes<HTMLInputElement>}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button data-testid={testIds.invoiceListCancelClosingNote}
            onClick={() => {
              setClosingNoteModalOpen(false);
              setClosingNote("");
            }}
          >
            {t("common:cancel")}
          </Button>
          <Button data-testid={testIds.invoiceListSaveClosingNote}
            onClick={() => {
              if (!selectedInvoiceForClosing) {
                return;
              }
              setClosingNoteModalOpen(false);
              onInvoiceStatusUpdate(
                selectedInvoiceForClosing,
                InvoiceStatus.Closed,
                closingNote
              );
              setClosingNote("");
            }}
            variant="contained"
            id="save-closing-note"
          >
            {t("common:save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InvoiceList;
